import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  FileText, Download, Search, Filter, Printer, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Clock, ThermometerSun, Users, Calendar, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime, formatDate, formatTime } from '@/utils/dateFormat';

export default function RelatorioCiclosPage() {
  const [ciclos, setCiclos] = useState([]);
  const [estatisticas, setEstatisticas] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoclaves, setAutoclaves] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [expandedCiclo, setExpandedCiclo] = useState(null);
  const [filters, setFilters] = useState({
    data_inicio: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    data_fim: new Date().toISOString().split('T')[0],
    autoclave_id: '',
    resultado: '',
    cliente_id: ''
  });

  useEffect(() => {
    fetchAutoclaves();
    fetchClientes();
    fetchRelatorio();
  }, []);

  const fetchAutoclaves = async () => {
    try {
      const res = await api.get('/autoclaves');
      setAutoclaves(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchClientes = async () => {
    try {
      const res = await api.get('/clientes');
      setClientes(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchRelatorio = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.autoclave_id) params.append('autoclave_id', filters.autoclave_id);
      if (filters.resultado) params.append('resultado', filters.resultado);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);

      const res = await api.get(`/relatorios/ciclos-completo?${params.toString()}`);
      setCiclos(res.data.ciclos || []);
      setEstatisticas(res.data.estatisticas || null);
    } catch (error) {
      toast.error('Erro ao carregar relatório de ciclos');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleFilter = () => { fetchRelatorio(); };

  const handleExportPDF = async () => {
    try {
      toast.info('Gerando PDF...');
      const params = new URLSearchParams();
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.autoclave_id) params.append('autoclave_id', filters.autoclave_id);
      if (filters.resultado) params.append('resultado', filters.resultado);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);

      const response = await api.get(`/relatorios/ciclos-completo/pdf?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `laudo-ciclos-${filters.data_inicio}-a-${filters.data_fim}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  const handleExportExcel = async () => {
    try {
      toast.info('Gerando Excel...');
      const params = new URLSearchParams();
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.autoclave_id) params.append('autoclave_id', filters.autoclave_id);
      if (filters.resultado) params.append('resultado', filters.resultado);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);

      const response = await api.get(`/relatorios/ciclos-completo/excel?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `laudo-ciclos-${filters.data_inicio}-a-${filters.data_fim}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar Excel');
    }
  };

  const handlePrintLaudo = (ciclo) => {
    const printWindow = window.open('', '_blank');
    const resultadoColor = ciclo.resultado === 'aprovado' ? '#16a34a' : ciclo.resultado === 'reprovado' ? '#dc2626' : '#f59e0b';
    const resultadoText = ciclo.resultado === 'aprovado' ? 'APROVADO' : ciclo.resultado === 'reprovado' ? 'REPROVADO' : 'PENDENTE';

    const clientesHtml = (ciclo.clientes_info || []).map(c => `
      <tr>
        <td style="padding:6px 12px;border:1px solid #d1d5db;">${c.nome}</td>
        <td style="padding:6px 12px;border:1px solid #d1d5db;">${c.cnpj_cpf || '-'}</td>
        <td style="padding:6px 12px;border:1px solid #d1d5db;">${(c.materiais || []).join(', ') || '-'}</td>
        <td style="padding:6px 12px;border:1px solid #d1d5db;">${(c.kits || []).join(', ') || '-'}</td>
      </tr>
    `).join('');

    const testeBioHtml = ciclo.teste_biologico ? `
      <div style="margin-top:16px;padding:12px;border:1px solid #d1d5db;border-radius:6px;">
        <h4 style="margin:0 0 8px;font-size:13px;color:#374151;">Teste Biológico</h4>
        <table style="width:100%;font-size:12px;border-collapse:collapse;">
          <tr><td style="padding:4px 8px;width:40%;color:#6b7280;">Resultado:</td><td style="padding:4px 8px;font-weight:bold;">${ciclo.teste_biologico.resultado || '-'}</td></tr>
          <tr><td style="padding:4px 8px;color:#6b7280;">Lote Indicador:</td><td style="padding:4px 8px;">${ciclo.teste_biologico.lote_indicador || '-'}</td></tr>
          <tr><td style="padding:4px 8px;color:#6b7280;">Tempo Incubação:</td><td style="padding:4px 8px;">${ciclo.teste_biologico.tempo_incubacao_horas || '-'} horas</td></tr>
          <tr><td style="padding:4px 8px;color:#6b7280;">Observações:</td><td style="padding:4px 8px;">${ciclo.teste_biologico.observacoes || '-'}</td></tr>
        </table>
      </div>
    ` : '';

    const testeQuiHtml = ciclo.teste_quimico ? `
      <div style="margin-top:12px;padding:12px;border:1px solid #d1d5db;border-radius:6px;">
        <h4 style="margin:0 0 8px;font-size:13px;color:#374151;">Teste Químico</h4>
        <table style="width:100%;font-size:12px;border-collapse:collapse;">
          <tr><td style="padding:4px 8px;width:40%;color:#6b7280;">Resultado:</td><td style="padding:4px 8px;font-weight:bold;">${ciclo.teste_quimico.resultado || '-'}</td></tr>
          <tr><td style="padding:4px 8px;color:#6b7280;">Classe Indicador:</td><td style="padding:4px 8px;">${ciclo.teste_quimico.classe_indicador || '-'}</td></tr>
          <tr><td style="padding:4px 8px;color:#6b7280;">Mudança de Cor:</td><td style="padding:4px 8px;">${ciclo.teste_quimico.mudanca_cor || '-'}</td></tr>
          <tr><td style="padding:4px 8px;color:#6b7280;">Observações:</td><td style="padding:4px 8px;">${ciclo.teste_quimico.observacoes || '-'}</td></tr>
        </table>
      </div>
    ` : '';

    const testeBDHtml = ciclo.teste_bowie_dick ? `
      <div style="margin-top:12px;padding:12px;border:1px solid #d1d5db;border-radius:6px;">
        <h4 style="margin:0 0 8px;font-size:13px;color:#374151;">Teste Bowie-Dick</h4>
        <table style="width:100%;font-size:12px;border-collapse:collapse;">
          <tr><td style="padding:4px 8px;width:40%;color:#6b7280;">Resultado:</td><td style="padding:4px 8px;font-weight:bold;">${ciclo.teste_bowie_dick.resultado || '-'}</td></tr>
          <tr><td style="padding:4px 8px;color:#6b7280;">Lote Indicador:</td><td style="padding:4px 8px;">${ciclo.teste_bowie_dick.lote_indicador || '-'}</td></tr>
          <tr><td style="padding:4px 8px;color:#6b7280;">Observações:</td><td style="padding:4px 8px;">${ciclo.teste_bowie_dick.observacoes || '-'}</td></tr>
        </table>
      </div>
    ` : '';

    const prof = ciclo.profissionais_etapa || {};
    const profissionaisHtml = Object.keys(prof).length > 0 ? `
      <div class="section">
        <div class="section-title">Profissionais Responsáveis</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
          ${prof.recepcao ? `<div class="info-row"><span class="info-label">Recepção:</span><span class="info-value">${prof.recepcao}</span></div>` : ''}
          ${prof.montagem ? `<div class="info-row"><span class="info-label">Montagem:</span><span class="info-value">${prof.montagem}</span></div>` : ''}
          ${prof.ciclo ? `<div class="info-row"><span class="info-label">Ciclo:</span><span class="info-value">${prof.ciclo}</span></div>` : ''}
          ${prof.arsenal ? `<div class="info-row"><span class="info-label">Arsenal:</span><span class="info-value">${prof.arsenal}</span></div>` : ''}
        </div>
      </div>
    ` : '';

    printWindow.document.write(`
      <html>
      <head>
        <title>Laudo de Esterilização - Ciclo ${ciclo.numero_ciclo}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; color: #1f2937; padding: 24px; font-size: 13px; }
          .header { text-align: center; border-bottom: 3px solid #1e40af; padding-bottom: 16px; margin-bottom: 20px; }
          .header h1 { font-size: 18px; color: #1e40af; margin-bottom: 4px; }
          .header h2 { font-size: 14px; color: #6b7280; font-weight: normal; }
          .resultado-badge { display: inline-block; padding: 6px 24px; border-radius: 4px; font-weight: bold; font-size: 16px; color: white; background: ${resultadoColor}; margin: 8px 0; }
          .section { margin-top: 20px; }
          .section-title { font-size: 14px; font-weight: bold; color: #1e40af; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; margin-bottom: 10px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; }
          .info-row { display: flex; gap: 8px; }
          .info-label { color: #6b7280; min-width: 140px; }
          .info-value { font-weight: 500; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 8px; }
          th { background: #1e40af; color: white; padding: 8px 12px; text-align: left; font-size: 11px; text-transform: uppercase; }
          td { padding: 6px 12px; border: 1px solid #d1d5db; }
          tr:nth-child(even) td { background: #f9fafb; }
          .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 12px; }
          .signature-area { margin-top: 48px; display: flex; justify-content: space-between; }
          .signature-line { width: 45%; text-align: center; }
          .signature-line hr { border: none; border-top: 1px solid #374151; margin-bottom: 4px; }
          .signature-line span { font-size: 11px; color: #6b7280; }
          @media print { body { padding: 12px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>LAUDO DE ESTERILIZAÇÃO</h1>
          <h2>Sterisul - Centro de Material e Esterilização</h2>
          <div class="resultado-badge">${resultadoText}</div>
        </div>

        <div class="section">
          <div class="section-title">Dados do Ciclo</div>
          <div class="info-grid">
            <div class="info-row"><span class="info-label">N° do Ciclo:</span><span class="info-value">${ciclo.numero_ciclo || '-'}</span></div>
            <div class="info-row"><span class="info-label">Data/Hora:</span><span class="info-value">${formatDateTime(ciclo.data_hora)}</span></div>
            <div class="info-row"><span class="info-label">Autoclave:</span><span class="info-value">${ciclo.autoclave_nome || '-'}</span></div>
            <div class="info-row"><span class="info-label">Método:</span><span class="info-value">${(ciclo.metodo || '-').toUpperCase()}</span></div>
            <div class="info-row"><span class="info-label">Temperatura:</span><span class="info-value">${ciclo.temperatura || '-'} °C</span></div>
            <div class="info-row"><span class="info-label">Pressão:</span><span class="info-value">${ciclo.pressao || '-'} atm</span></div>
            <div class="info-row"><span class="info-label">Tempo:</span><span class="info-value">${ciclo.tempo_minutos || '-'} min</span></div>
            <div class="info-row"><span class="info-label">N° Biológico:</span><span class="info-value">${ciclo.numero_biologico || '-'}</span></div>
            <div class="info-row"><span class="info-label">Hora Início:</span><span class="info-value">${ciclo.hora_inicio || '-'}</span></div>
            <div class="info-row"><span class="info-label">Hora Fim:</span><span class="info-value">${ciclo.hora_fim || '-'}</span></div>
            <div class="info-row"><span class="info-label">Validade:</span><span class="info-value">${formatDate(ciclo.data_validade)}</span></div>
            <div class="info-row"><span class="info-label">Bowie-Dick:</span><span class="info-value">${ciclo.is_bowie_dick ? 'Sim' : 'Não'}</span></div>
          </div>
        </div>

        ${(ciclo.clientes_info || []).length > 0 ? `
        <div class="section">
          <div class="section-title">Clientes Atendidos & Materiais</div>
          <table>
            <thead>
              <tr><th>Cliente</th><th>CPF/CNPJ</th><th>Materiais</th><th>Kits/Caixas</th></tr>
            </thead>
            <tbody>${clientesHtml}</tbody>
          </table>
        </div>
        ` : ''}

        ${profissionaisHtml}

        ${testeBioHtml || testeQuiHtml || testeBDHtml ? `
        <div class="section">
          <div class="section-title">Indicadores e Testes</div>
          ${testeBioHtml}${testeQuiHtml}${testeBDHtml}
        </div>
        ` : ''}

        <div class="signature-area">
          <div class="signature-line">
            <hr />
            <span>Responsável Técnico</span>
          </div>
          <div class="signature-line">
            <hr />
            <span>Data: ____/____/________</span>
          </div>
        </div>

        <div class="footer">
          Documento gerado em ${formatDateTime(new Date().toISOString())} | Sterisul CME - Sistema de Gestão de Esterilização
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  const getResultadoBadge = (resultado) => {
    if (resultado === 'aprovado') return <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Aprovado</Badge>;
    if (resultado === 'reprovado') return <Badge className="bg-red-100 text-red-800 border-red-200"><XCircle className="w-3 h-3 mr-1" />Reprovado</Badge>;
    return <Badge className="bg-amber-100 text-amber-800 border-amber-200"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
  };

  return (
    <div data-testid="relatorio-ciclos-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Relatório de Ciclos
          </h1>
          <p className="text-lg text-slate-600 mt-2">Laudo completo de esterilização com dados detalhados</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExportExcel} className="bg-green-600 hover:bg-green-700" data-testid="export-ciclos-excel">
            <Download className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button onClick={handleExportPDF} className="bg-red-600 hover:bg-red-700" data-testid="export-ciclos-pdf">
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-600" />
          Filtros
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <Label htmlFor="rc_inicio">Data Início</Label>
            <Input id="rc_inicio" type="date" value={filters.data_inicio} onChange={(e) => setFilters(f => ({ ...f, data_inicio: e.target.value }))} className="mt-1" data-testid="ciclos-date-start" />
          </div>
          <div>
            <Label htmlFor="rc_fim">Data Fim</Label>
            <Input id="rc_fim" type="date" value={filters.data_fim} onChange={(e) => setFilters(f => ({ ...f, data_fim: e.target.value }))} className="mt-1" data-testid="ciclos-date-end" />
          </div>
          <div>
            <Label htmlFor="rc_autoclave">Autoclave</Label>
            <select id="rc_autoclave" value={filters.autoclave_id} onChange={(e) => setFilters(f => ({ ...f, autoclave_id: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="ciclos-autoclave-filter">
              <option value="">Todas</option>
              {autoclaves.map(a => (<option key={a.id} value={a.id}>{a.identificacao || a.nome}</option>))}
            </select>
          </div>
          <div>
            <Label htmlFor="rc_resultado">Resultado</Label>
            <select id="rc_resultado" value={filters.resultado} onChange={(e) => setFilters(f => ({ ...f, resultado: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="ciclos-resultado-filter">
              <option value="">Todos</option>
              <option value="aprovado">Aprovado</option>
              <option value="reprovado">Reprovado</option>
              <option value="pendente">Pendente</option>
            </select>
          </div>
          <div>
            <Label htmlFor="rc_cliente">Cliente</Label>
            <select id="rc_cliente" value={filters.cliente_id} onChange={(e) => setFilters(f => ({ ...f, cliente_id: e.target.value }))} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" data-testid="ciclos-cliente-filter">
              <option value="">Todos</option>
              {clientes.map(c => (<option key={c.id} value={c.id}>{c.nome}</option>))}
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={handleFilter} className="w-full" data-testid="filter-ciclos-btn">
              <Search className="w-4 h-4 mr-2" />
              Filtrar
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      {estatisticas && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-100 rounded-lg"><Activity className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Total</p>
                <p className="text-2xl font-bold text-slate-900">{estatisticas.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-100 rounded-lg"><CheckCircle className="w-5 h-5 text-green-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Aprovados</p>
                <p className="text-2xl font-bold text-green-600">{estatisticas.aprovados}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-100 rounded-lg"><XCircle className="w-5 h-5 text-red-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Reprovados</p>
                <p className="text-2xl font-bold text-red-600">{estatisticas.reprovados}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-100 rounded-lg"><Clock className="w-5 h-5 text-amber-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Pendentes</p>
                <p className="text-2xl font-bold text-amber-600">{estatisticas.em_processo}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-100 rounded-lg"><ThermometerSun className="w-5 h-5 text-indigo-600" /></div>
              <div>
                <p className="text-xs text-slate-500 uppercase font-medium">Taxa Aprov.</p>
                <p className="text-2xl font-bold text-indigo-600">{estatisticas.taxa_aprovacao}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ciclos - Formato Laudo */}
      {ciclos.map((ciclo) => (
        <div key={ciclo.id} className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid={`ciclo-laudo-${ciclo.id}`}>
          {/* Ciclo Header */}
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-2.5 rounded-lg ${ciclo.resultado === 'aprovado' ? 'bg-green-600' : ciclo.resultado === 'reprovado' ? 'bg-red-600' : 'bg-amber-500'}`}>
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Ciclo {ciclo.numero_ciclo}</h3>
                <p className="text-sm text-slate-500">{formatDateTime(ciclo.data_hora)} - {ciclo.autoclave_nome || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getResultadoBadge(ciclo.resultado)}
              <Button variant="outline" size="sm" onClick={() => handlePrintLaudo(ciclo)} data-testid={`print-laudo-${ciclo.id}`}>
                <Printer className="w-4 h-4 mr-1" />
                Laudo
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpandedCiclo(expandedCiclo === ciclo.id ? null : ciclo.id)}
                data-testid={`toggle-ciclo-${ciclo.id}`}
              >
                {expandedCiclo === ciclo.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Resumo compacto sempre visível */}
          <div className="px-6 py-3 bg-slate-50 border-b border-slate-100">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
              <div><span className="text-slate-500">Método: </span><span className="font-medium">{(ciclo.metodo || '-').toUpperCase()}</span></div>
              <div><span className="text-slate-500">Temp: </span><span className="font-medium">{ciclo.temperatura}°C</span></div>
              <div><span className="text-slate-500">Pressão: </span><span className="font-medium">{ciclo.pressao} atm</span></div>
              <div><span className="text-slate-500">Tempo: </span><span className="font-medium">{ciclo.tempo_minutos} min</span></div>
              <div><span className="text-slate-500">Validade: </span><span className="font-medium">{formatDate(ciclo.data_validade)}</span></div>
              <div><span className="text-slate-500">Clientes: </span><span className="font-medium">{(ciclo.clientes_info || []).length || 0}</span></div>
            </div>
          </div>

          {/* Detalhes expandidos */}
          {expandedCiclo === ciclo.id && (
            <div className="px-6 py-5 space-y-5">
              {/* Grid de dados completos */}
              <div>
                <h4 className="text-sm font-semibold text-blue-700 uppercase tracking-wider mb-3">Dados Completos do Ciclo</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">N° Ciclo</p>
                    <p className="font-semibold">{ciclo.numero_ciclo}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">N° Biológico</p>
                    <p className="font-semibold">{ciclo.numero_biologico || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Hora Início</p>
                    <p className="font-semibold">{ciclo.hora_inicio || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Hora Fim</p>
                    <p className="font-semibold">{ciclo.hora_fim || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Autoclave</p>
                    <p className="font-semibold">{ciclo.autoclave_nome || '-'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Bowie-Dick</p>
                    <p className="font-semibold">{ciclo.is_bowie_dick ? 'Sim' : 'Não'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Arsenal</p>
                    <p className="font-semibold">{ciclo.enviado_arsenal ? 'Enviado' : 'Não enviado'}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Validade</p>
                    <p className="font-semibold">{formatDate(ciclo.data_validade)}</p>
                  </div>
                </div>
              </div>

              {/* Clientes e Materiais */}
              {(ciclo.clientes_info || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-blue-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Clientes Atendidos & Materiais
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase">Cliente</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase">CPF/CNPJ</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase">Materiais</th>
                          <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase">Kits/Caixas</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {ciclo.clientes_info.map((c, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="px-4 py-3 font-medium">{c.nome}</td>
                            <td className="px-4 py-3 text-slate-600">{c.cnpj_cpf || '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{(c.materiais || []).join(', ') || '-'}</td>
                            <td className="px-4 py-3 text-slate-600">{(c.kits || []).join(', ') || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Profissionais por Etapa */}
              {ciclo.profissionais_etapa && Object.keys(ciclo.profissionais_etapa).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-blue-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Profissionais por Etapa
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {ciclo.profissionais_etapa.recepcao && (
                      <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <p className="text-xs text-indigo-600 font-semibold uppercase mb-1">Recepção</p>
                        <p className="text-sm font-medium text-slate-900">{ciclo.profissionais_etapa.recepcao}</p>
                      </div>
                    )}
                    {ciclo.profissionais_etapa.montagem && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-xs text-amber-600 font-semibold uppercase mb-1">Montagem</p>
                        <p className="text-sm font-medium text-slate-900">{ciclo.profissionais_etapa.montagem}</p>
                      </div>
                    )}
                    {ciclo.profissionais_etapa.ciclo && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-600 font-semibold uppercase mb-1">Ciclo</p>
                        <p className="text-sm font-medium text-slate-900">{ciclo.profissionais_etapa.ciclo}</p>
                      </div>
                    )}
                    {ciclo.profissionais_etapa.arsenal && (
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <p className="text-xs text-purple-600 font-semibold uppercase mb-1">Arsenal</p>
                        <p className="text-sm font-medium text-slate-900">{ciclo.profissionais_etapa.arsenal}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Testes/Indicadores */}
              {(ciclo.teste_biologico || ciclo.teste_quimico || ciclo.teste_bowie_dick) && (
                <div>
                  <h4 className="text-sm font-semibold text-blue-700 uppercase tracking-wider mb-3">Indicadores e Testes</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {ciclo.teste_biologico && (
                      <div className="p-4 border border-slate-200 rounded-lg">
                        <h5 className="font-semibold text-sm mb-2">Teste Biológico</h5>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Resultado:</span> <span className="font-medium">{ciclo.teste_biologico.resultado}</span></p>
                          <p><span className="text-slate-500">Lote:</span> {ciclo.teste_biologico.lote_indicador || '-'}</p>
                          <p><span className="text-slate-500">Incubação:</span> {ciclo.teste_biologico.tempo_incubacao_horas || '-'}h</p>
                        </div>
                      </div>
                    )}
                    {ciclo.teste_quimico && (
                      <div className="p-4 border border-slate-200 rounded-lg">
                        <h5 className="font-semibold text-sm mb-2">Teste Químico</h5>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Resultado:</span> <span className="font-medium">{ciclo.teste_quimico.resultado}</span></p>
                          <p><span className="text-slate-500">Classe:</span> {ciclo.teste_quimico.classe_indicador || '-'}</p>
                          <p><span className="text-slate-500">Cor:</span> {ciclo.teste_quimico.mudanca_cor || '-'}</p>
                        </div>
                      </div>
                    )}
                    {ciclo.teste_bowie_dick && (
                      <div className="p-4 border border-slate-200 rounded-lg">
                        <h5 className="font-semibold text-sm mb-2">Bowie-Dick</h5>
                        <div className="space-y-1 text-sm">
                          <p><span className="text-slate-500">Resultado:</span> <span className="font-medium">{ciclo.teste_bowie_dick.resultado}</span></p>
                          <p><span className="text-slate-500">Lote:</span> {ciclo.teste_bowie_dick.lote_indicador || '-'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Empty State */}
      {!loading && ciclos.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <Activity className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-slate-600">Nenhum ciclo encontrado</h3>
          <p className="text-sm text-slate-500 mt-2">Ajuste os filtros ou registre novos ciclos</p>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
}
